import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UserSearchHit } from '@citibox-panel/api-client';
import { detectPhone } from '../lib/detectPhone';

export type PhoneSearchStatus = 'idle' | 'searching' | 'resolved';

export interface UsePhoneSearch {
  submit: (rawInput: string) => Promise<UserSearchHit[] | null>;
  status: PhoneSearchStatus;
  hits: UserSearchHit[];
}

const FIVE_MINUTES = 5 * 60 * 1000;

// Search-on-submit (not typeahead): `submit` resolves the hits and returns them
// so the caller acts once, synchronously with the resolution — including when
// the result comes from cache (fetchQuery returns it without a refetch, so there
// is no loading→resolved transition to miss). `status`/`hits` drive the dropdown.
export function usePhoneSearch(
  search: (phone: string) => Promise<UserSearchHit[]>,
): UsePhoneSearch {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<PhoneSearchStatus>('idle');
  const [hits, setHits] = useState<UserSearchHit[]>([]);

  const submit = useCallback(
    async (rawInput: string): Promise<UserSearchHit[] | null> => {
      const phone = detectPhone(rawInput);
      if (phone === null) return null;

      setStatus('searching');
      try {
        const results = await queryClient.fetchQuery({
          queryKey: ['phone-search', phone],
          queryFn: () => search(phone),
          staleTime: FIVE_MINUTES,
        });
        setHits(results);
        setStatus('resolved');
        return results;
      } catch (error) {
        // A failed search must leave the loading state: otherwise `status` stays
        // 'searching' and the dropdown shows "Buscando…" forever. Rethrow so the
        // caller can surface a recoverable error instead of acting on no hits.
        setStatus('idle');
        throw error;
      }
    },
    [queryClient, search],
  );

  return { submit, status, hits };
}
